import * as React from 'react';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import DragHandleIcon from '@mui/icons-material/DragHandle';
import SaveIcon from '@mui/icons-material/Save';
import RestartAltIcon from '@mui/icons-material/RestartAlt';
import { Box, Button, IconButton, MenuItem, Stack, Switch, Table, TableBody, TableCell, TableHead, TableRow, TextField, Typography } from '@mui/material';
import { DndContext, DragEndEvent, PointerSensor, closestCenter, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, useSortable, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { getMatchAdminPlayers, updateMatchAdminPlayers } from '../../Api';
import { ADMIN_PLAYER_LINEUP_TYPES, AdminMatchPlayerInfoDto, AdminMatchPlayersDto } from '../../models/dtos/AdminMatchStatDto';
import { CenteredProgress, EmptyState, PlayerAvatar, TeamAvatar, formatLabel } from './MatchAdminTabCommon';

type EditablePlayerRow = AdminMatchPlayerInfoDto & {
    rowId: string;
};

function toDraftRows(playersInfo: AdminMatchPlayersDto['playersInfo']): EditablePlayerRow[] {
    return playersInfo.map((player, index) => ({
        ...player,
        rowId: `${player.id ?? 'player'}-${index}`
    }));
}

function toPayload(rows: EditablePlayerRow[]): AdminMatchPlayersDto['playersInfo'] {
    return rows.map(({ rowId, ...player }) => player);
}

function SortablePlayerRow(props: {
    row: EditablePlayerRow;
    availableTeams: AdminMatchPlayersDto['availableTeams'];
    onUpdate: (patch: Partial<EditablePlayerRow>) => void;
    onDelete: () => void;
}) {
    const { row, availableTeams, onUpdate, onDelete } = props;
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: row.rowId });
    const style: React.CSSProperties = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const selectedTeam = availableTeams.find(team => team.syncId === row.teamId)?.teamInfo ?? row.teamInfo;

    return (
        <TableRow ref={setNodeRef} style={style}>
            <TableCell sx={{ width: 32, cursor: 'grab', color: 'text.disabled' }} {...attributes} {...listeners}>
                <DragHandleIcon fontSize="small" />
            </TableCell>
            <TableCell sx={{ minWidth: 72 }}>
                <PlayerAvatar player={row} />
            </TableCell>
            <TableCell sx={{ minWidth: 220 }}>
                <TextField size="small" fullWidth value={row.name ?? ''} onChange={event => onUpdate({ name: event.target.value })} />
            </TableCell>
            <TableCell sx={{ minWidth: 160 }}>
                <TextField
                    select
                    size="small"
                    fullWidth
                    value={row.lineupType ?? 'NOT_IN_LINEUP'}
                    onChange={event => onUpdate({ lineupType: event.target.value as AdminMatchPlayerInfoDto['lineupType'] })}
                >
                    {ADMIN_PLAYER_LINEUP_TYPES.map(option => (
                        <MenuItem key={option} value={option}>
                            {formatLabel(option)}
                        </MenuItem>
                    ))}
                </TextField>
            </TableCell>
            <TableCell align="center" sx={{ minWidth: 120 }}>
                <Switch
                    checked={row.hasPlaytime ?? false}
                    onChange={event => onUpdate({ hasPlaytime: event.target.checked })}
                    inputProps={{ 'aria-label': `Toggle playtime for ${row.name || row.id}` }}
                />
            </TableCell>
            <TableCell sx={{ minWidth: 200 }}>
                <TextField size="small" fullWidth value={row.id ?? ''} onChange={event => onUpdate({ id: event.target.value })} />
            </TableCell>
            <TableCell sx={{ minWidth: 240 }}>
                <Stack direction="row" spacing={1} alignItems="center">
                    <TeamAvatar team={selectedTeam} />
                    <TextField
                        select
                        fullWidth
                        size="small"
                        value={row.teamId ?? ''}
                        onChange={event => {
                            const selected = availableTeams.find(team => team.syncId === event.target.value);
                            onUpdate({ teamId: event.target.value, teamInfo: selected?.teamInfo ?? null });
                        }}
                    >
                        <MenuItem value="">No team</MenuItem>
                        {availableTeams.map(team => (
                            <MenuItem key={team.syncId} value={team.syncId}>
                                {team.teamInfo.name ?? team.syncId}
                            </MenuItem>
                        ))}
                    </TextField>
                </Stack>
            </TableCell>
            <TableCell sx={{ minWidth: 260 }}>
                <TextField size="small" fullWidth value={row.imgUrl ?? ''} onChange={event => onUpdate({ imgUrl: event.target.value })} />
            </TableCell>
            <TableCell align="right">
                <IconButton color="error" onClick={onDelete}>
                    <DeleteOutlineIcon />
                </IconButton>
            </TableCell>
        </TableRow>
    );
}

export default function MatchPlayersTab(props: {
    matchId: number;
    onError: (message: string) => void;
    onSuccess: (message: string) => void;
}) {
    const { matchId, onError, onSuccess } = props;
    const [adminPlayers, setAdminPlayers] = React.useState<AdminMatchPlayersDto | null>(null);
    const [loading, setLoading] = React.useState(true);
    const [draftRows, setDraftRows] = React.useState<EditablePlayerRow[]>([]);
    const [saving, setSaving] = React.useState(false);

    const sensors = useSensors(useSensor(PointerSensor));

    const onDragEnd = (event: DragEndEvent) => {
        const { active, over } = event;
        if (over != null && active.id !== over.id) {
            setDraftRows(current => {
                const oldIndex = current.findIndex(row => row.rowId === active.id);
                const newIndex = current.findIndex(row => row.rowId === over.id);
                return arrayMove(current, oldIndex, newIndex);
            });
        }
    };

    React.useEffect(() => {
        setLoading(true);
        getMatchAdminPlayers(matchId)
            .then(result => { setAdminPlayers(result); setLoading(false); })
            .catch(() => { setAdminPlayers(null); setLoading(false); });
    }, [matchId]);

    React.useEffect(() => {
        setDraftRows(adminPlayers?.playersInfo ? toDraftRows(adminPlayers.playersInfo) : []);
    }, [adminPlayers]);

    if (loading) {
        return <CenteredProgress />;
    }

    if (adminPlayers == null) {
        return <EmptyState message="No players available for the main event." />;
    }

    const payload = toPayload(draftRows);
    const originalPayload = adminPlayers.playersInfo;
    const isDirty = JSON.stringify(payload) !== JSON.stringify(originalPayload);

    const updateRow = (rowId: string, patch: Partial<EditablePlayerRow>) => {
        setDraftRows(current => current.map(row => row.rowId === rowId ? { ...row, ...patch } : row));
    };

    const onAddRow = () => {
        const defaultTeam = adminPlayers.availableTeams[0];
        setDraftRows(current => current.concat({
            rowId: `new-${Date.now()}-${current.length}`,
            id: '',
            teamId: defaultTeam?.syncId ?? '',
            name: '',
            nameTranslations: null,
            imgUrl: '',
            teamInfo: defaultTeam?.teamInfo ?? null,
            lineupType: 'NOT_IN_LINEUP',
            hasPlaytime: false
        }));
    };

    const onReset = () => {
        setDraftRows(toDraftRows(adminPlayers.playersInfo));
    };

    const onSave = async () => {
        const trimmedIds = payload.map(player => player.id?.trim() ?? '');
        if (trimmedIds.some(id => id.length === 0)) {
            onError('Each player must have an id.');
            return;
        }

        if (new Set(trimmedIds).size !== trimmedIds.length) {
            onError('Player ids must be unique.');
            return;
        }

        setSaving(true);
        try {
            const request = payload.map(player => ({
                ...player,
                id: player.id.trim(),
                name: player.name?.trim() ?? '',
                teamId: player.teamId?.trim() || null,
                imgUrl: player.imgUrl?.trim() || null,
                lineupType: player.lineupType ?? 'NOT_IN_LINEUP',
                hasPlaytime: player.hasPlaytime ?? false
            }));
            const result = await updateMatchAdminPlayers(matchId, request);
            setAdminPlayers(prev => prev == null ? prev : { ...prev, playersInfo: result.playersInfo });
            onSuccess('Players update sent to provider.');
        } catch (error) {
            onError(error instanceof Error ? error.message : 'Failed to update players.');
        } finally {
            setSaving(false);
        }
    };

    return (
        <Stack spacing={2}>
            <Typography variant="subtitle2" color="text.secondary">
                Main variety: {adminPlayers.varietyName}
            </Typography>
            <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<AddIcon />} onClick={onAddRow}>Add player</Button>
                <Button variant="outlined" color="inherit" startIcon={<RestartAltIcon />} onClick={onReset} disabled={!isDirty || saving}>Reset</Button>
                <Button variant="contained" startIcon={<SaveIcon />} onClick={onSave} disabled={!isDirty || saving}>Save</Button>
            </Stack>
            {draftRows.length === 0 ? (
                <EmptyState message="No players available for the main event." />
            ) : (
                <Box sx={{ overflowX: 'auto' }}>
                    <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
                        <SortableContext items={draftRows.map(r => r.rowId)} strategy={verticalListSortingStrategy}>
                            <Table size="small">
                                <TableHead>
                                    <TableRow>
                                        <TableCell sx={{ width: 32 }} />
                                        <TableCell>Player</TableCell>
                                        <TableCell>Name</TableCell>
                                        <TableCell>Lineup Type</TableCell>
                                        <TableCell align="center">Has Playtime</TableCell>
                                        <TableCell>Player ID</TableCell>
                                        <TableCell>Team</TableCell>
                                        <TableCell>Image URL</TableCell>
                                        <TableCell align="right">Actions</TableCell>
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {draftRows.map(row => (
                                        <SortablePlayerRow
                                            key={row.rowId}
                                            row={row}
                                            availableTeams={adminPlayers.availableTeams}
                                            onUpdate={patch => updateRow(row.rowId, patch)}
                                            onDelete={() => setDraftRows(current => current.filter(item => item.rowId !== row.rowId))}
                                        />
                                    ))}
                                </TableBody>
                            </Table>
                        </SortableContext>
                    </DndContext>
                </Box>
            )}
        </Stack>
    );
}
